import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  FileCheck,
  LayoutDashboard,
  Upload,
  History,
  GitCompare,
  LogOut,
  User,
  Menu,
  X,
  Coins,
  Shield,
  Database,
  Trash2,
  Gavel,
  Briefcase
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../../config';

// Admin emails
const ADMIN_EMAILS = ['konechoco@gmail.com', 'konechocoproduction@gmail.com'];

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/upload', label: 'Nuova Busta', icon: Upload },
  { path: '/history', label: 'Storico', icon: History },
  { path: '/compare', label: 'Confronta', icon: GitCompare },
  { path: '/vertenze', label: 'Vertenze', icon: Gavel },
  { path: '/banca-dati', label: 'Banca Dati', icon: Database },
  { path: '/credits', label: 'Crediti', icon: Coins },
  // Visibile a tutti: l'accesso reale e' verificato dal backend
  // (get_operator_user, allowlist OPERATOR_EMAILS/ADMIN_EMAILS). Chi non
  // e' abilitato vede un messaggio di accesso riservato nella pagina.
  { path: '/operatore', label: 'Area Consulenza', icon: Briefcase },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [credits, setCredits] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const isAdmin = user?.role === 'admin' || (user?.email && ADMIN_EMAILS.includes(user.email));
  const isOperatorOrAdmin = user?.role === 'admin' || user?.role === 'operator' || (user?.email && ADMIN_EMAILS.includes(user.email));

  const filteredNavItems = navItems.filter(item => {
    if (item.path === '/vertenze' || item.path === '/operatore') {
      return isOperatorOrAdmin;
    }
    return true;
  });

  useEffect(() => {
    // Reset credits when user changes
    setCredits(null);
    if (user) {
      fetchCredits();
    }
  }, [location.pathname, user?.id]);

  const fetchCredits = async () => {
    try {
      const response = await axios.get(`${API_URL}/credits`);
      setCredits(response.data.credits);
    } catch (err) {
      console.error('Error fetching credits:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'ELIMINA') return;
    setDeletingAccount(true);
    try {
      await axios.delete(`${API_URL}/auth/delete-account`);
      logout();
      navigate('/');
      toast.success('Account eliminato con successo.');
    } catch (err) {
      console.error('Errore eliminazione account:', err);
      toast.error('Errore durante l\'eliminazione. Riprova.');
      setDeletingAccount(false);
    }
  };

  return (
    <div className="app-layout">
      {/* Sidebar - Desktop */}
      <aside className="sidebar hidden lg:flex" data-testid="sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-forest rounded-sm flex items-center justify-center">
              <FileCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-bold text-lg tracking-tight text-forest">BustaControllo</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          {/* Admin Link */}
          {isAdmin && (
            <Link 
              to="/admin" 
              className={`flex items-center gap-3 p-3 mb-3 rounded-sm transition-colors ${
                location.pathname === '/admin' 
                  ? 'bg-amber-100 text-amber-800' 
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
              }`}
            >
              <Shield className="w-4 h-4" />
              <span className="text-sm font-medium">Pannello Admin</span>
            </Link>
          )}
          
          {/* Credit Balance */}
          <Link 
            to="/credits" 
            className="flex items-center justify-between p-3 mb-3 bg-mint/50 rounded-sm hover:bg-mint transition-colors"
          >
            <div className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-forest" />
              <span className="text-sm font-medium text-forest">Crediti</span>
            </div>
            <span className="font-mono font-bold text-forest">{credits ?? '-'}</span>
          </Link>
          
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-9 h-9 bg-mint rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-forest" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-muted-foreground hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            Esci
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-xs text-muted-foreground/60 hover:text-red-500 hover:bg-red-50 mt-1"
            onClick={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }}
            data-testid="delete-account-btn"
          >
            <Trash2 className="w-3 h-3" strokeWidth={1.5} />
            Elimina account
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-forest rounded-sm flex items-center justify-center">
              <FileCheck className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-bold tracking-tight text-forest">BustaControllo</span>
          </Link>
          <div className="flex items-center gap-2">
            {/* Credit Badge Mobile */}
            <Link 
              to="/credits" 
              className="flex items-center gap-1 px-2 py-1 bg-mint rounded-full"
            >
              <Coins className="w-3.5 h-3.5 text-forest" />
              <span className="font-mono text-sm font-bold text-forest">{credits ?? '-'}</span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-white border-b border-slate-200 shadow-lg">
            <nav className="py-2">
              {filteredNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 text-sm ${
                      isActive ? 'bg-mint text-forest font-medium' : 'text-muted-foreground'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              <button
                className="flex items-center gap-3 px-4 py-3 text-sm text-red-600 w-full"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
                <span>Esci</span>
              </button>
              <button
                className="flex items-center gap-3 px-4 py-3 text-xs text-muted-foreground/60 hover:text-red-500 w-full"
                onClick={() => { setMobileMenuOpen(false); setDeleteConfirmText(''); setShowDeleteModal(true); }}
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                <span>Elimina account</span>
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="main-content lg:ml-0">
        <div className="lg:hidden h-14" /> {/* Spacer for mobile header */}
        <div className="p-4 pb-24 sm:p-6 lg:p-8 lg:pb-8 min-h-[calc(100vh-3.5rem)] lg:min-h-screen flex flex-col">
          <div className="flex-1">
            {children}
          </div>
          {/* Copyright Footer */}
          <footer className="mt-8 pt-6 border-t border-slate-200 text-center">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} BustaControllo. Tutti i diritti riservati.
            </p>
          </footer>
        </div>
      </main>

      {/* Modale Elimina Account */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}
        >
          <div style={{
            backgroundColor: 'white', borderRadius: '0.75rem',
            padding: '2rem', maxWidth: '400px', width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{
                width: '2.5rem', height: '2.5rem', borderRadius: '50%',
                backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Trash2 style={{ width: '1.25rem', height: '1.25rem', color: '#dc2626' }} />
              </div>
              <h2 style={{ fontWeight: 'bold', fontSize: '1.125rem', color: '#111827' }}>
                Elimina Account
              </h2>
            </div>

            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.75rem', lineHeight: '1.5' }}>
              Questa azione è <strong>irreversibile</strong>. Tutti i tuoi dati, buste paga e crediti verranno eliminati permanentemente.
            </p>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              Digita <strong>ELIMINA</strong> per confermare:
            </p>

            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="ELIMINA"
              style={{
                width: '100%', padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db', borderRadius: '0.375rem',
                fontSize: '0.875rem', marginBottom: '1.25rem',
                outline: 'none', boxSizing: 'border-box',
              }}
              autoFocus
            />

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  padding: '0.5rem 1rem', borderRadius: '0.375rem',
                  border: '1px solid #d1d5db', backgroundColor: 'white',
                  cursor: 'pointer', fontSize: '0.875rem', color: '#374151',
                }}
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'ELIMINA' || deletingAccount}
                style={{
                  padding: '0.5rem 1rem', borderRadius: '0.375rem',
                  border: 'none',
                  backgroundColor: deleteConfirmText === 'ELIMINA' ? '#dc2626' : '#fca5a5',
                  color: 'white', cursor: deleteConfirmText === 'ELIMINA' ? 'pointer' : 'not-allowed',
                  fontSize: '0.875rem', fontWeight: '500',
                }}
              >
                {deletingAccount ? 'Eliminazione...' : 'Elimina definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
